import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { get, set, ref } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import ProfileChecklist from './ProfileChecklist';

import { buildTutorialSteps, shouldAutoShowTour, TUTORIAL_VERSION, REPLAY_EVENT } from '../config/tutorialSteps';
import { canUseQuo } from '../services/quoSms';

// First-sign-in walkthrough. Shows once per user (users/{uid}/onboarding/tour = { completedAt, version, newAgent }),
// and again only when TUTORIAL_VERSION is bumped. Anyone can replay it (replayTutorial() / "Replay the Resi tour").
// Steps come from the role-filtered config in src/config/tutorialSteps.js.
const btn = { display: 'inline-block', padding: '10px 18px', borderRadius: 8, fontWeight: 700, fontSize: 14, border: '1px solid #B8962E', background: '#B8962E', color: '#FFFFFF', cursor: 'pointer', fontFamily: 'inherit' };
const btnGhost = { ...btn, background: '#FFFFFF', color: '#5B6270', borderColor: '#D5D9E0' };
const panel = { position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(20,20,20,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' };
const card = { width: '100%', maxWidth: 520, overflowX: 'hidden', maxHeight: '90vh', overflowY: 'auto', background: '#FFFFFF', color: '#1A1A1A', borderRadius: 14, borderTop: '4px solid #D4AF37', padding: 24, boxSizing: 'border-box', fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' };
const row = { display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 18, justifyContent: 'space-between', alignItems: 'center' };

export default function FirstSignInTour() {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [step, setStep] = useState('welcome');
  const [newAgent, setNewAgent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    get(ref(database, `users/${user.uid}/onboarding/tour`))
      .then((snap) => { if (!cancelled && shouldAutoShowTour(snap.val())) setShow(true); })
      .catch((err) => console.error('Could not check the welcome tour', err));
    return () => { cancelled = true; };
  }, [user?.uid]);

  useEffect(() => {
    const replay = () => { setStep('welcome'); setShow(true); };
    window.addEventListener(REPLAY_EVENT, replay);
    return () => window.removeEventListener(REPLAY_EVENT, replay);
  }, []);

  const plan = buildTutorialSteps({ role: userRole, quo: canUseQuo(), uid: user?.uid, email: user?.email });
  const onLogin = location.pathname === '/login' || location.pathname === '/' || location.pathname === '/set-password';
  if (!show || !user?.uid || onLogin) return null;

  const finish = async () => {
    setBusy(true);
    try {
      await set(ref(database, `users/${user.uid}/onboarding/tour`), { completedAt: new Date().toISOString(), version: TUTORIAL_VERSION, newAgent: newAgent === true });
    } catch (err) {
      console.error('Could not save the welcome tour', err);
    } finally {
      setBusy(false);
      setShow(false);
    }
  };

  const go = (path) => navigate(path);

  let content = null;
  if (step === 'welcome') {
    content = (
      <>
        <h2 style={{ margin: '0 0 8px', color: '#1A1A1A' }}>Welcome to Resi</h2>
        <p style={{ margin: 0, lineHeight: 1.5 }}>This quick walkthrough takes about two minutes. We'll set up your profile and walk through the parts of Resi you can use. You can skip it at any point.</p>
        <div style={row}>
          <button type="button" onClick={finish} style={btnGhost} disabled={busy}>Skip for now</button>
          <button type="button" onClick={() => setStep('profile')} style={btn}>Start</button>
        </div>
      </>
    );
  } else if (step === 'profile') {
    content = (
      <>
        <h2 style={{ margin: '0 0 8px', color: '#1A1A1A' }}>Set up your profile</h2>
        <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>Add your name, phone, photo, and contact details. Clients and the team see this information, so it's worth filling in now.</p>
        <ProfileChecklist uid={user.uid} onboarding showGoToProfile={false} />
        <div style={row}>
          <button type="button" onClick={() => { go('/profile'); }} style={btnGhost}>Go to Profile</button>
          <button type="button" onClick={() => setStep(plan.askNewAgent ? 'question' : plan.training ? 'training' : 'menu')} style={btn}>Next</button>
        </div>
      </>
    );
  } else if (step === 'question') {
    content = (
      <>
        <h2 style={{ margin: '0 0 8px', color: '#1A1A1A' }}>Are you a new agent?</h2>
        <p style={{ margin: 0, lineHeight: 1.5 }}>New agents get the onboarding course first. Either way, everyone completes Resi Setup next.</p>
        <div style={row}>
          <button type="button" onClick={() => { setNewAgent(false); setStep('training'); }} style={btnGhost}>No, I'm experienced</button>
          <button type="button" onClick={() => { setNewAgent(true); setStep('onboarding'); }} style={btn}>Yes, I'm new</button>
        </div>
      </>
    );
  } else if (step === 'onboarding') {
    content = (
      <>
        <h2 style={{ margin: '0 0 8px', color: '#1A1A1A' }}>Your onboarding</h2>
        <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>Start with Agent Onboarding for the step-by-step course, then Resi Setup for Phone Link, your Resi profile, and your AI connection.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button type="button" onClick={() => go('/training/onboarding')} style={btnGhost}>Open Agent Onboarding</button>
          <button type="button" onClick={() => go('/training/resi-setup')} style={btnGhost}>Open Resi Setup</button>
        </div>
        <div style={row}>
          <span />
          <button type="button" onClick={() => setStep('training')} style={btn}>Next</button>
        </div>
      </>
    );
  } else if (step === 'training') {
    const t = plan.training;
    content = (
      <>
        <h2 style={{ margin: '0 0 8px', color: '#1A1A1A' }}>{t.title}</h2>
        <div style={{ background: '#FFF6DA', border: '2px solid #D4AF37', borderRadius: 10, padding: 14, margin: '0 0 12px' }}>
          <strong style={{ display: 'block', marginBottom: 6, color: '#8C6D1F' }}>Please don't skip this one.</strong>
          <span style={{ lineHeight: 1.5 }}>{t.text}</span>
        </div>
        <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>Training also holds the courses and the knowledge base. Add to your knowledge base as you learn.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button type="button" onClick={() => go(t.path)} style={btn}>Open Resi Setup</button>
          <button type="button" onClick={() => go('/training')} style={btnGhost}>Open Training</button>
        </div>
        <div style={row}>
          <span />
          <button type="button" onClick={() => setStep('menu')} style={btn}>Next</button>
        </div>
      </>
    );
  } else if (step === 'menu') {
    content = (
      <>
        <h2 style={{ margin: '0 0 8px', color: '#1A1A1A' }}>A tour of what you can use</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {plan.sections.map((m) => (
            <div key={m.id}>
              <button type="button" onClick={() => go(m.path)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: '#8C6D1F', textAlign: 'left' }}>{m.title}</button>
              <div style={{ fontSize: 14, lineHeight: 1.45 }}>{m.text}</div>
            </div>
          ))}
        </div>
        <div style={row}>
          <span />
          <button type="button" onClick={() => setStep('done')} style={btn}>Next</button>
        </div>
      </>
    );
  } else {
    content = (
      <>
        <h2 style={{ margin: '0 0 8px', color: '#1A1A1A' }}>Next step</h2>
        <p style={{ margin: 0, lineHeight: 1.5 }}>You can replay this tour any time from your Profile{plan.training ? ' or Resi Setup' : ''}. Enjoy Resi.</p>
        <p style={{ margin: '12px 0 0', lineHeight: 1.5, fontWeight: 600 }}>{plan.training ? 'Next step: open Training, then Resi Setup, and fill in everything it asks for.' : 'Next step: click Profile in the left menu and complete your full profile.'}</p>
        <div style={row}>
          <span />
          <button type="button" onClick={finish} style={btn} disabled={busy}>{busy ? 'Saving...' : 'Finish'}</button>
        </div>
      </>
    );
  }

  return (
    <div style={panel} role="dialog" aria-modal="true" aria-label="Welcome walkthrough">
      <div style={card}>{content}</div>
    </div>
  );
}
